import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, ArrowRight, Send, Loader2, CheckCircle2 } from "lucide-react";
import StepProgress from "../components/request/StepProgress";
import EmailGateStep from "../components/request/EmailGateStep";
import RequestBasicsStep from "../components/request/RequestBasicsStep";
import CategoryStep from "../components/request/CategoryStep";
import TriageStep from "../components/request/TriageStep";
import SummaryStep from "../components/request/SummaryStep";
import { fetchRequestStatus, StatusResultCard } from "../components/request/StatusCheck";

const API = process.env.REACT_APP_BACKEND_URL;

const initialData = {
  reqName: "",
  reqRole: "",
  sponsor: "",
  targetDate: "",
  justification: "",
  urgency: "",
  categoryId: "",
  categoryName: "",
  subtypes: [],
  categoryFields: {},
  scale: "",
  budget: "",
  supportNeeds: "",
  reportingNeeds: "",
  dependencies: [],
};

export default function Request() {
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [data, setData] = useState(initialData);
  const [stepError, setStepError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submittedId, setSubmittedId] = useState("");
  const [statusResult, setStatusResult] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");

  const refreshStatus = useCallback(async () => {
    if (!submittedId || !email) return;
    setStatusLoading(true);
    setStatusError("");
    try {
      const result = await fetchRequestStatus(submittedId, email);
      setStatusResult(result);
    } catch (e) {
      setStatusError(e.message);
    } finally {
      setStatusLoading(false);
    }
  }, [submittedId, email]);

  useEffect(() => {
    if (step === 5) refreshStatus();
  }, [step, refreshStatus]);

  const update = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const toggleSubtype = (val) => {
    setData((d) => ({
      ...d,
      subtypes: d.subtypes.includes(val) ? d.subtypes.filter((s) => s !== val) : [...d.subtypes, val],
    }));
  };

  const toggleDependency = (val) => {
    setData((d) => ({
      ...d,
      dependencies: d.dependencies.includes(val) ? d.dependencies.filter((s) => s !== val) : [...d.dependencies, val],
    }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!data.reqName || !data.reqRole || !data.justification || !data.urgency || !data.targetDate) {
        setStepError("Please fill in all required fields (marked with *) before continuing.");
        return false;
      }
    }
    if (step === 2) {
      if (!data.categoryId) {
        setStepError("Please select a request category before continuing.");
        return false;
      }
    }
    if (step === 3) {
      if (!data.scale || !data.budget) {
        setStepError("Please fill in expected scale and budget status before continuing.");
        return false;
      }
    }
    setStepError("");
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setStepError("");
    setStep((s) => Math.max(1, s - 1));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API}/api/request/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok) {
        setSubmitError(result.detail || "Something went wrong. Please try again.");
      } else {
        setSubmittedId(result.id);
        setStep(5);
      }
    } catch {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overflow-x-hidden" data-testid="request-page">
      <section className="pt-32 pb-16" style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}>
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-5" style={{ color: "#D4EC8E" }}>Technofocus &middot; Service Request Intake</p>
          <h1 className="text-4xl sm:text-5xl font-light text-white tracking-tight mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            What do you need us to<br /><span style={{ color: "#D4EC8E", fontWeight: 600 }}>build, deliver, or run?</span>
          </h1>
          <p className="text-lg" style={{ color: "rgba(185,220,210,0.85)" }}>
            {"One form for every kind of request across content, delivery, programmes, live events, and net-new platforms \u2014 exclusively for Microsoft requesters."}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          {step > 0 && step < 5 && <StepProgress current={step - 1} />}

          <div className="rounded-3xl border p-6 sm:p-10" style={{ borderColor: "#E2EBE9" }}>
            {step === 0 && (
              <EmailGateStep
                email={email}
                setEmail={setEmail}
                onVerified={(tok) => {
                  setToken(tok);
                  setStep(1);
                }}
              />
            )}
            {step === 1 && <RequestBasicsStep data={data} update={update} />}
            {step === 2 && <CategoryStep data={data} update={update} toggleSubtype={toggleSubtype} />}
            {step === 3 && <TriageStep data={data} update={update} toggleDependency={toggleDependency} />}
            {step === 4 && <SummaryStep data={data} email={email} error={submitError} />}

            {step === 5 && (
              <div className="text-center py-10" data-testid="request-success-screen">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "#194449" }}>
                  <CheckCircle2 className="text-white" size={28} />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Request Submitted</h2>
                <p className="text-gray-500 max-w-sm mx-auto mb-1">
                  Thanks, {data.reqName.split(" ")[0] || "there"}. Our triage team has received your request and will follow up at <span className="font-medium text-gray-700">{email}</span>.
                </p>
                <p className="text-xs text-gray-400 mt-4" data-testid="request-success-id">Reference ID: {submittedId}</p>

                <div className="max-w-sm mx-auto mt-6">
                  {statusLoading && !statusResult && (
                    <p className="text-sm text-gray-400 flex items-center justify-center gap-2" data-testid="request-success-status-loading">
                      <Loader2 size={14} className="animate-spin" /> {"Fetching current status\u2026"}
                    </p>
                  )}
                  {statusError && !statusResult && (
                    <p className="text-sm" style={{ color: "#B5453D" }} data-testid="request-success-status-error">{statusError}</p>
                  )}
                  {statusResult && (
                    <StatusResultCard result={statusResult} onRefresh={refreshStatus} refreshing={statusLoading} />
                  )}
                </div>
              </div>
            )}

            {step >= 1 && step <= 4 && (
              <>
                {stepError && <p className="text-sm mt-2 mb-2" style={{ color: "#B5453D" }} data-testid="request-step-error">{stepError}</p>}
                <div className="flex items-center justify-between mt-8 pt-6 border-t" style={{ borderColor: "#E2EBE9" }}>
                  <button
                    onClick={goBack}
                    data-testid="request-back-button"
                    className="flex items-center gap-2 text-sm font-medium px-5 py-3 rounded-xl border transition-colors"
                    style={{ borderColor: "#DCE3E1", color: "#5B6664" }}
                  >
                    <ArrowLeft size={15} /> Back
                  </button>

                  {step < 4 ? (
                    <button
                      onClick={goNext}
                      data-testid="request-next-button"
                      className="flex items-center gap-2 text-sm font-semibold text-white px-6 py-3 rounded-xl transition-colors"
                      style={{ backgroundColor: "#194449" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#0B2E38")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#194449")}
                    >
                      Next <ArrowRight size={15} />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      data-testid="request-submit-button"
                      className="flex items-center gap-2 text-sm font-semibold text-white px-6 py-3 rounded-xl transition-colors disabled:opacity-60"
                      style={{ backgroundColor: "#194449" }}
                      onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#0B2E38"; }}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#194449")}
                    >
                      {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                      {submitting ? "Submitting\u2026" : "Submit Request"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
